import { h, Component } from 'preact';
import Store from 'store/store';
import 'store/trackEvents';
import { joinClasses, loadScript } from 'utils/utils';
import { loadPage, getPageComponent, PAGE_ERROR, PAGE_LOADING } from 'utils/pageComponents';
import { getPageTitle } from 'utils/metaData';
import { trackPageView } from 'utils/tracking';
import { setLandingLocation } from 'utils/geo';
import tabbingDetection from 'utils/tabbingDetection';

import Header from 'components/header/header';
import Footer from 'components/footer/footer';

import s from 'base/focusFeaturesHub/focusFeaturesHubApp.sss';

const isServer = typeof window === 'undefined';
const pagesWithCustomFooter = { showtimes: true, homeEnt: true };

export default class FocusFeaturesHubApp extends Component {
	constructor(props) {
		super(props);

		this.state = {
			appState: props.initialStore,
		};

		this.setActivePageFromURL = this.setActivePageFromURL.bind(this);
		this.onClick = this.onClick.bind(this);

		Store.set(props.initialStore);
		// Store.get().appData.set('pages', new (class Pages { constructor(o){ Object.assign(this,o); } get boltShowtimes() { console.trace('ACCESSING boltShowtimes !'); return undefined; } })(Store.get().appData.pages));
		Store.on('update', () => this.setState({ appState: Store.get() }));

		if (!isServer) {
			this.setActivePageFromURL();
			this.loadPage();
		}
	}

	componentDidMount() {
		window.addEventListener('popstate', this.setActivePageFromURL);
		window.addEventListener('hashchange', this.setActivePageFromURL);
		setLandingLocation();
		tabbingDetection();	
		if (__IS_LOCAL__ || __IS_DEMO__ || location.hostname.startsWith('stage.')) {
			loadScript('https://www.bugherd.com/sidebarv2.js?apikey=qxburjxpwkenyxujs8wt8q', { async: true });
		}
	}

	componentWillUpdate(nextProps, nextState) {
		if (nextState.appState.activePage !== this.state.appState.activePage) {
			this.loadPage(nextState.appState.activePage);
		}
	}

	componentWillUnmount() {
		window.removeEventListener('popstate', this.setActivePageFromURL);
		window.removeEventListener('hashchange', this.setActivePageFromURL);
	}

	getActivePage(state = this.state) {
		return state.appState.activePage;
	}
	getActivePageData(state = this.state) {
		return state.appState.appData.pages?.[this.getActivePage(state)];
	}

	async loadPage(page = this.getActivePage()) {
		await loadPage(page);
		if (page === this.getActivePage()) {
			this.forceUpdate();
		}
	}

	isNewDestination(destination) {
		if (!destination) return false;
		const store = this.state.appState;
		if (store.activePage !== destination.id) return true;
		if (destination.props?.slug !== store.activePageProps?.slug) return true;
		return false;
	}

	setActivePageFromURL(e) {
		// TODO: cache url -> id mappings ?
		const pages = Object.values(this.state.appState.appData.pages || {}).filter(p => !p.base || p.base === 'focusFeaturesHub');
		let currentPath = '/' + (location.origin + location.pathname).replace(window.__BASE_URL__, '').replace(/index\.html$/, '');

		// On local/demo, we put the path for film pages in the hash, since the actual html files don't exist and we don't run the lambda function
		if (location.hash.startsWith('#/films/')) {
			currentPath = location.hash.slice(1);
		}

		let destination;
		if (currentPath.startsWith('/films/')) {
			const [, , slug, subPath = ''] = currentPath.split('/');
			const pageId = { '': 'movie', tickets: 'showtimes', 'watch-at-home': 'homeEnt', 'home-ent': 'homeEnt' }[subPath];
			const page = pageId && pages.find(p => p.id === pageId);
			if (page) {
				destination = { ...page, props: { slug } };
			} else {
				destination = pages.find(p => p.id === 'error-404');
			}
		} else {
			destination = pages.find(p => currentPath === p.path + (p.baseHtml && p.baseHtml !== 'index.html' ? p.baseHtml : ''));
		}

		if (this.isNewDestination(destination)) {
			// Scroll to top ?
			window.scrollTo(0, 0);
			Store.get().set({ activePage: destination.id, activePageProps: destination.props || {} }).now();
			// NOTE: the movie page (and other movie-specific pages) sets the page title on its own when data is loaded
			if (destination && !destination.props?.slug) {
				document.title = getPageTitle(destination);
			}
			// window.dispatchEvent(new CustomEvent('powster-navigation'));

			trackPageView(destination);
		}
	}

	onClick(e) {
		const link = e.target.closest('a');
		if (!link || link.dataset.uncontrolled) return;
		const target = link.target || '_self';
		if (target !== '_self' || link.origin !== location.origin) return;

		e.preventDefault();

		if (link.href === location.href) {
			return;
		}

		// Preserve cms-preview query param across navigation
		const targetUrl = new URL(link.href);
		const cmsPreview = new URLSearchParams(location.search).get('cms-preview');
		if (cmsPreview !== null) targetUrl.searchParams.set('cms-preview', cmsPreview);

		history.pushState(null, '', targetUrl.href);
		this.setActivePageFromURL();
	}

	// skipToContent(e) {
	// 	// We might not need this (depends on desired UX): google.com seems to do something similar, but web.dev does not
	// 	e.preventDefault();
	// 	e.stopPropagation();

	// 	// TODO: should also check CSS display/visibility of ancestors
	// 	const firstInteractiveElement = document.querySelector('#content ~ * :is(a[href], button, input, [tabindex]):not(:disabled, [tabindex^="-"])');
	// 	firstInteractiveElement?.focus();
	// }

	renderLoading() {
		return <div class={s.loading} key="loading" />;
	}

	renderError() {
		// TODO: improve (maybe external component?)
		return <div class={s.error} key="error">Error loading page</div>;
	}

	getActivePageKey() {
		const store = this.state.appState;
		return [
			store.activePage,
			store.activePageProps?.slug,
		].filter(Boolean).join('-');
	}

	renderPage() {
		if (isServer) return this.renderLoading();
		const activePage = this.getActivePage();
		const Page = getPageComponent(activePage);
		if (!Page) return;
		// TODO: improve loading/error screens (maybe external components?)
		if (Page === PAGE_LOADING) return this.renderLoading();
		if (Page === PAGE_ERROR) return this.renderError();
		return <Page key={this.getActivePageKey()} store={this.state.appState} data={this.getActivePageData()?.data} {...this.state.appState.activePageProps} />;
	}

	render(props, state) {
		return (
			<div class={joinClasses(s.container)} id="hub" onClick={this.onClick}>
				{/* <a class={s.jumpToContent} href="#content" data-uncontrolled onClick={this.skipToContent}>Skip to content</a> */}
				<div class={s.aboveTheFold}>
					<Header store={state.appState} />
					{/* <a id="content" /> */}
					{this.renderPage()}
				</div>
				{!pagesWithCustomFooter[this.getActivePage()] && <Footer slug={this.state.appState.activePageProps?.slug} />}
			</div>
		);
	}
}
